package de.xtl.retroguide.question.scenes

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.unit.dp
import de.xtl.retroguide.protocol.AnswerCategory
import kotlin.math.PI
import kotlin.math.sin

private val SkyTop = Color(0xFF90CAF9)
private val SkyBottom = Color(0xFFE3F2FD)
private val SeaTop = Color(0xFF42A5F5)
private val SeaBottom = Color(0xFF0D47A1)
private val Hull = Color(0xFF6D4C41)
private val Sail = Color(0xFFFFFDF7)
private val Sand = Color(0xFFFFE082)
private val PalmLeaf = Color(0xFF2E7D32)
private val PalmTrunk = Color(0xFF8D6E63)
private val RockColor = Color(0xFF78909C)
private val Chain = Color(0xFF37474F)

@Composable
internal fun SailboatScene(counts: Map<AnswerCategory, Int>, modifier: Modifier = Modifier) {
    val transition = rememberInfiniteTransition()
    val wave by transition.animateFloat(
        initialValue = 0f,
        targetValue = (2 * PI).toFloat(),
        animationSpec = infiniteRepeatable(tween(4_000, easing = LinearEasing)),
    )
    val tilt by transition.animateFloat(
        initialValue = -4f,
        targetValue = 4f,
        animationSpec = infiniteRepeatable(tween(2_200, easing = FastOutSlowInEasing), RepeatMode.Reverse),
    )
    val gust by transition.animateFloat(
        initialValue = 0f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(tween(1_800, easing = LinearEasing)),
    )
    Box(modifier.fillMaxWidth().aspectRatio(16f / 9f).clip(RoundedCornerShape(24.dp))) {
        Canvas(Modifier.fillMaxSize()) {
            val waterline = size.height * .58f
            drawRect(Brush.verticalGradient(listOf(SkyTop, SkyBottom), endY = waterline))
            drawIsland(waterline, tilt)
            drawWind(gust)
            drawSea(waterline, wave)
            val bob = sin(wave) * size.height * .012f
            drawAnchor(boatX = size.width * .42f, waterline = waterline + bob)
            drawBoat(boatX = size.width * .42f, waterline = waterline + bob, tilt = tilt)
            drawRocks(waterline)
        }
        SceneCornerBadges(
            topStart = AnswerCategory.WIND,
            topEnd = AnswerCategory.ISLAND,
            bottomStart = AnswerCategory.ANCHOR,
            bottomEnd = AnswerCategory.ROCK,
            counts = counts,
        )
    }
}

private fun DrawScope.drawSea(waterline: Float, wave: Float) {
    val amplitude = size.height * .02f
    val path = Path().apply {
        moveTo(0f, waterline)
        val steps = 60
        for (step in 0..steps) {
            val x = size.width * step / steps
            lineTo(x, waterline + sin(x / size.width * 4 * PI.toFloat() + wave) * amplitude)
        }
        lineTo(size.width, size.height)
        lineTo(0f, size.height)
        close()
    }
    drawPath(path, Brush.verticalGradient(listOf(SeaTop, SeaBottom), startY = waterline, endY = size.height))
}

private fun DrawScope.drawWind(gust: Float) {
    repeat(3) { line ->
        val phase = (gust + line / 3f) % 1f
        val y = size.height * (.2f + line * .09f)
        val startX = size.width * (.03f + phase * .18f)
        drawLine(
            color = Color.White.copy(alpha = 1f - phase * .7f),
            start = Offset(startX, y),
            end = Offset(startX + size.width * .1f, y),
            strokeWidth = size.height * .012f,
            cap = StrokeCap.Round,
        )
    }
}

private fun DrawScope.drawIsland(waterline: Float, tilt: Float) {
    drawOval(Sand, topLeft = Offset(size.width * .74f, waterline - size.height * .07f), size = Size(size.width * .24f, size.height * .14f))
    val crown = Offset(size.width * .875f, waterline - size.height * .27f)
    drawLine(PalmTrunk, Offset(size.width * .86f, waterline - size.height * .04f), crown, strokeWidth = size.width * .012f, cap = StrokeCap.Round)
    repeat(5) { leaf ->
        rotate(leaf * 72f + tilt * 1.5f, pivot = crown) {
            drawOval(PalmLeaf, topLeft = Offset(crown.x, crown.y - size.height * .018f), size = Size(size.width * .075f, size.height * .036f))
        }
    }
}

private fun DrawScope.drawBoat(boatX: Float, waterline: Float, tilt: Float) {
    rotate(tilt, pivot = Offset(boatX, waterline)) {
        val deck = waterline - size.height * .05f
        val hull = Path().apply {
            moveTo(boatX - size.width * .13f, deck)
            lineTo(boatX + size.width * .13f, deck)
            lineTo(boatX + size.width * .09f, waterline + size.height * .03f)
            lineTo(boatX - size.width * .09f, waterline + size.height * .03f)
            close()
        }
        drawPath(hull, Hull)
        val mastTop = deck - size.height * .36f
        drawLine(Hull, Offset(boatX, deck), Offset(boatX, mastTop), strokeWidth = size.width * .006f)
        val mainSail = Path().apply {
            moveTo(boatX + size.width * .006f, mastTop + size.height * .02f)
            lineTo(boatX + size.width * .006f, deck - size.height * .03f)
            lineTo(boatX + size.width * .11f, deck - size.height * .03f)
            close()
        }
        drawPath(mainSail, Sail)
        val jib = Path().apply {
            moveTo(boatX - size.width * .006f, mastTop + size.height * .07f)
            lineTo(boatX - size.width * .006f, deck - size.height * .03f)
            lineTo(boatX - size.width * .08f, deck - size.height * .03f)
            close()
        }
        drawPath(jib, Sail.copy(alpha = .9f))
        val flag = Path().apply {
            moveTo(boatX, mastTop)
            lineTo(boatX - size.width * .035f, mastTop + size.height * .015f)
            lineTo(boatX, mastTop + size.height * .03f)
            close()
        }
        drawPath(flag, Color(0xFFE53935))
    }
}

private fun DrawScope.drawAnchor(boatX: Float, waterline: Float) {
    val top = Offset(boatX - size.width * .06f, waterline)
    val anchor = Offset(boatX - size.width * .1f, size.height * .84f)
    val stroke = size.width * .006f
    drawLine(Chain, top, anchor, strokeWidth = stroke * .7f, pathEffect = PathEffect.dashPathEffect(floatArrayOf(10f, 6f)))
    val height = size.height * .1f
    drawCircle(Chain, radius = height * .12f, center = anchor, style = Stroke(stroke))
    drawLine(Chain, anchor + Offset(0f, height * .12f), anchor + Offset(0f, height), strokeWidth = stroke)
    drawLine(Chain, anchor + Offset(-height * .25f, height * .3f), anchor + Offset(height * .25f, height * .3f), strokeWidth = stroke)
    drawArc(
        Chain,
        startAngle = 0f,
        sweepAngle = 180f,
        useCenter = false,
        topLeft = anchor + Offset(-height * .4f, height * .6f),
        size = Size(height * .8f, height * .8f),
        style = Stroke(stroke, cap = StrokeCap.Round),
    )
}

private fun DrawScope.drawRocks(waterline: Float) {
    val rocks = Path().apply {
        moveTo(size.width * .58f, waterline + size.height * .12f)
        lineTo(size.width * .61f, waterline - size.height * .02f)
        lineTo(size.width * .64f, waterline + size.height * .04f)
        lineTo(size.width * .67f, waterline - size.height * .05f)
        lineTo(size.width * .71f, waterline + size.height * .12f)
        close()
    }
    drawPath(rocks, RockColor)
}
